import { Link as LinkIcon, Video } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import type { MasterclassModuleMode } from "@/types/masterclasses"

/**
 * OKS-55 — Module type pill.
 *
 * Maps `mode` → shadcn `Badge` + lucide icon. Used on the inline modules
 * list (Masterclass Show) + the New/Edit form's right-rail summary.
 *
 * UX Spec §6 a11y — colour is never the sole indicator: the pill pairs
 * an icon and a text label ("Link" / "Upload"). UX Spec §2.1 type pill
 * row.
 *
 * `mode === null` (fresh form state with no choice yet) renders nothing —
 * the type pill is only meaningful once the user picks a mode.
 */
export interface MasterclassModuleTypeBadgeProps {
  mode: MasterclassModuleMode
  /** Optional `data-testid` override for scoped DOM queries in specs. */
  "data-testid"?: string
}

export function MasterclassModuleTypeBadge({
  mode,
  "data-testid": testId,
}: MasterclassModuleTypeBadgeProps) {
  const { t } = useTranslation()

  if (mode === null) return null

  const Icon = mode === "link" ? LinkIcon : Video
  // Inline both `t()` calls in the ternary so the i18n health scanner
  // sees both literal keys at the call site (it can't follow a
  // variable-assigned key string).
  const label =
    mode === "link"
      ? t("masterclasses.show.modules.row.type_link")
      : t("masterclasses.show.modules.row.type_upload")

  return (
    <Badge
      variant="secondary"
      className="inline-flex items-center gap-1"
      data-testid={testId ?? `masterclass-module-type-badge-${mode}`}
    >
      <Icon className="size-3" aria-hidden="true" />
      <span>{label}</span>
    </Badge>
  )
}
